<div class="scholarships-view">

  @if (saveMessage) {
    <div class="alert alert-success">
      <i class="fas fa-check-circle"></i> {{ saveMessage }}
    </div>
  }

  <div class="header-row">
    <div>
      <h2 class="section-heading">Scholarships/ Opportunities</h2>
      <p class="section-subtitle">Manage scholarship and their assigned application templates.</p>
    </div>
    @if (authorization.hasPermission(permissions.ScholarshipCreate)) {
      <button type="button" class="btn btn-primary" (click)="openCreateScholarship()">
        <i class="fas fa-plus"></i> Create New Scholarship
      </button>
    }
  </div>

  @if (availableDraft) {
    <div class="alert alert-warning" style="margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center;">
      <span><span class="badge badge-warning">DRAFT</span> {{ availableDraft.displayName || 'Untitled scholarship' }} is saved and ready to continue.</span>
      <button type="button" class="btn btn-secondary btn-sm" (click)="resumeDraft()">Resume draft</button>
    </div>
  }

  <div class="card">
    <div class="card-header">All Scholarships <span class="count-pill">{{ scholarshipsCount }}</span></div>
    <div class="card-body" style="padding: 0;">
      <table class="table scholarships-table">
        <thead>
          <tr>
            <th>Scholarship Name</th>
            <th>Country</th>
            <th>Application Template</th>
            <th>Assigned Form</th>
            <th>Applications</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (scholarshipsCount === 0) {
            <tr>
              <td colspan="7" class="empty-cell">No scholarships created yet. Click "Create New Scholarship" to get
                started.
              </td>
            </tr>
          }
          @for (scholarship of paginatedScholarships; track scholarship) {
            <tr>
              <td class="scholarship-name">
                <a href="#" class="inline-link" (click)="$event.preventDefault(); viewScholarship(scholarship)">
                  {{ scholarship.title }}
                </a>
              </td>
              <td>
                <span class="country-cell">
                  <strong>{{ scholarship.scholarshipTemplate.country.name.slice(0,2).toUpperCase() }}</strong> {{
                  scholarship.scholarshipTemplate.country.name }}
                </span>
              </td>
              <td [class.unassigned-text]="scholarship.scholarshipTemplate.id === null">{{
              scholarship.scholarshipTemplate.name }}</td>
              <td>
                <span class="badge" [class.badge-success]="scholarship.id !== 0"
                  [class.badge-danger]="scholarship.id === 0">
                  {{ scholarship.id !== 0 ? 'Assigned' : 'Unassigned' }}
                </span>
              </td>
              <td>
                <span class="badge" [class.badge-success]="scholarship.isAcceptingApplications"
                  [class.badge-danger]="!scholarship.isAcceptingApplications">
                  {{ scholarship.isAcceptingApplications ? 'Open' : 'Closed' }}
                </span>
                <small class="hint-text">{{ applicationWindowLabel(scholarship) }}</small>
              </td>
              <td>
                <span class="badge" [class.badge-success]="scholarship.id !== 0"
                  [class.badge-warning]="scholarship.id === 0">
                  {{ scholarship.id !== 0 ? 'Completed' : 'Draft' }}
                </span>
              </td>
              <td class="action-cell">
                <button type="button" class="btn btn-primary btn-sm"
                  (click)="viewScholarship(scholarship)">View</button>
                @if (authorization.hasPermission(permissions.ScholarshipEdit)) {
                  <button type="button" class="btn btn-secondary btn-sm"
                  (click)="openEditScholarship(scholarship)">Edit</button>
                  @if (scholarship.isAcceptingApplications) {
                    <button type="button" class="btn btn-warning btn-sm"
                      [disabled]="savingApplicationWindowId === scholarship.id"
                      title="Close applications for this scholarship now"
                      (click)="setApplicationWindow(scholarship, 'FORCE_CLOSED')">
                      <i class="fas fa-lock"></i> Close
                    </button>
                  } @else {
                    <button type="button" class="btn btn-success btn-sm"
                      [disabled]="savingApplicationWindowId === scholarship.id"
                      title="Re-open applications for this scholarship, even after its closing date"
                      (click)="setApplicationWindow(scholarship, 'FORCE_OPEN')">
                      <i class="fas fa-lock-open"></i> Open
                    </button>
                  }
                  @if (scholarship.applicationWindow !== 'AUTOMATIC') {
                    <button type="button" class="btn btn-secondary btn-sm"
                      [disabled]="savingApplicationWindowId === scholarship.id"
                      title="Follow the closing date again"
                      (click)="setApplicationWindow(scholarship, 'AUTOMATIC')">
                      <i class="fas fa-clock"></i> Automatic
                    </button>
                  }
                }
                @if (authorization.hasPermission(permissions.ScholarshipDelete)) {
                  <button type="button" class="btn btn-danger btn-sm"
                  (click)="requestDelete(scholarship.id)">Delete</button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>

      @if (scholarshipsCount > pageSize) {
        <app-pagination [currentPage]="currentPage" [totalPages]="totalPages"
          [totalItems]="scholarshipsCount" [showingFrom]="showingFrom" [showingTo]="showingTo" itemLabel="scholarships"
          (pageChange)="goToPage($event)">
        </app-pagination>
      }
    </div>
  </div>
</div>

<!-- Create / Edit Scholarship Modal -->
@if (showScholarshipModal) {
  <div class="modal-overlay" (click)="closeModal()">
    <div class="modal-content scholarship-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title">{{ isEditing ? 'Edit Scholarship' : 'Create New Scholarship' }}</div>
        <button type="button" class="modal-close" (click)="closeModal()">&times;</button>
      </div>
      <div class="modal-body">
        @if (draftMessage) {
          <div class="alert alert-success"><i class="fas fa-check-circle"></i> {{ draftMessage }}</div>
        }
        @if (errorMessage) {
          <div class="alert alert-danger">
            <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
          </div>
        }
        <div class="card" style="margin-bottom: 0;">
          <div class="card-header">Template Assignment</div>
          <div class="card-body">
            <div class="assignment-info">
              <i class="fas fa-info-circle"></i>
              Select an application template to assign to this scholarship.
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label>Application Template</label>
                <select class="form-control" [(ngModel)]="selectedTemplateId"
                  (ngModelChange)="onTemplateSelected($event)">
                  <option [ngValue]="null">-- No template assigned --</option>
                  @for (t of filteredTemplates; track t) {
                    <option [ngValue]="t.id">{{ t.name }} ({{ t.country.name }})
                    </option>
                  }
                </select>
                @if (filteredTemplates.length === 0) {
                  <small class="hint-text">
                    No templates created yet. Create one under "Scholarship Templates" first.
                  </small>
                }
              </div>
              <div class="form-group">
                <label>Country <span class="required">*</span></label>
                <select class="form-control" [(ngModel)]="countryId" disabled="disabled">
                  @for (thisCountry of countries; track thisCountry) {
                    <option [ngValue]="thisCountry.id">{{ thisCountry.name }}</option>
                  }
                </select>
              </div>
            </div>
            <div class="form-group">
              <label>Template Description</label>
              <textarea class="form-control" rows="3" [value]="templateDescription || 'No description provided.'"
              disabled></textarea>
            </div>
            @if (selectedTemplate; as t) {
              <div class="template-preview">
                <div class="preview-grid">
                  <div><span class="preview-label">Template</span>
                  <div class="preview-value">{{ t.name }}</div>
                </div>
                <div><span class="preview-label">Min Average</span>
                <div class="preview-value">{{ t.gradePointAverage.minimumPercentage !== null ?
                t.gradePointAverage.minimumPercentage + '%' : 'Not set' }}</div>
              </div>
              <div><span class="preview-label">Template Fields</span>
              <div class="preview-value">{{ t.fields.length }} fields</div>
            </div>
            <div><span class="preview-label">Documents</span>
            <div class="preview-value">{{ getRequiredDocCount(t) }} required</div>
          </div>
        </div>
      </div>
    }
  </div>
</div>
<div class="card" style="margin-bottom: 16px;">
  <div class="card-header">Basic Scholarship Details</div>
  <div class="card-body">
    <div class="form-grid">
      <div class="form-group">
        <label> Code <span class="required">*</span></label>
        <input type="text" class="form-control" [(ngModel)]="code" placeholder="e.g. na-scholarship-2025">
      </div>
      <div class="form-group">
        <label> Title <span class="required">*</span></label>
        <input type="text" class="form-control" [(ngModel)]="title"
          placeholder="e.g. Software Engineer Intern - Paris">
      </div>
    </div>
    <div class="form-grid">
      <div class="form-group">
        <label>Provider <span class="required">*</span></label>
        <select class="form-control" [(ngModel)]="providerId">
          @for (provider of scholarshipProviders; track provider) {
            <option [ngValue]="provider.id">
              {{ provider.name }}
            </option>
          }
        </select>
      </div>
      <div class="form-group">
        <label>Funding Amount <span class="required">*</span></label>
        <input type="text" class="form-control" [(ngModel)]="fundingAmount" placeholder="e.g. 50000">
      </div>
    </div>
    <div class="form-grid">
      <div class="form-group">
        <label>Application Process <span class="required">*</span></label>
        <input type="text" class="form-control" [(ngModel)]="applicationProcess"
          placeholder="e.g. Click here to apply...">
      </div>
      <div class="form-group">
        <label>Application Deadline <span class="required">*</span></label>
        <input type="date" class="form-control" [(ngModel)]="deadline" placeholder="e.g. Application Deadline..."
          [min]="minDeadline">
      </div>
    </div>
    <div class="form-grid">
      <div class="form-group">
        <label>Application Url <span class="required">*</span></label>
        <input type="text" class="form-control" [(ngModel)]="applicationUrl"
          placeholder="e.g. Click here to apply...">
      </div>
      <div class="form-group">
        <label>Image Url <span class="required">*</span></label>
        <input type="text" class="form-control" [(ngModel)]="imageUrl"
          placeholder="e.g. https://example.com/image.png">
        </div>
      </div>
      <div class="form-group" style="margin-top: 14px;">
        <label>Description</label>
        <textarea class="form-control" rows="3" [(ngModel)]="description"
        placeholder="Describe the scholarship..."></textarea>
      </div>
      <div class="form-group" style="margin-top: 14px;">
        <label>Overview</label>
        <textarea class="form-control" rows="3" [(ngModel)]="overview"
        placeholder="Scholarship overview..."></textarea>
      </div>
      <div class="form-group" style="margin-top: 14px;">
        <label>Eligibilities</label>
        <select class="form-control" multiple [(ngModel)]="selectedEligibilities"
          [ngModelOptions]="{standalone: true}">
        @for (eligibility of allEligibilities; track eligibility) {
          <option [ngValue]="eligibility.id">
            {{ eligibility.description}}
          </option>
        }
      </select>
    </div>
  </div>
  <div class="form-grid">
    <div class="form-group">
      <label>Allocation Frequencies</label>
      <select class="form-control" [(ngModel)]="selectedAllocationFrequencyId">
        <option [ngValue]="null">-- No frequency assigned --</option>
        @for (freq of allocationFrequencies; track freq) {
          <option [ngValue]="freq.id">{{ freq.name }}</option>
        }
      </select>
    </div>
    <div class="form-group">
      <label>Categories</label>
      <select class="form-control" [(ngModel)]="selectedCategoryId">
        <option [ngValue]="null">-- No categories assigned --</option>
        @for (category of scholarshipCategories; track category) {
          <option [ngValue]="category.id">{{ category.name }}
          </option>
        }
      </select>
    </div>
  </div>
</div>
<div class="modal-footer">
  <button type="button" class="btn btn-secondary" (click)="closeModal()">Cancel</button>
  @if (!isEditing) {
    <button type="button" class="btn btn-secondary" (click)="saveDraft()"><i class="fas fa-save"></i> Save Draft</button>
  }
  <button type="button" class="btn btn-primary" (click)="saveScholarship()">
    <i class="fas fa-save"></i> {{ isEditing ? 'Update Scholarship' : 'Save Scholarship' }}
  </button>
</div>
</div>
</div>
</div>
}

<!-- Delete Confirmation Modal -->
@if (showDeleteConfirm) {
  <div class="modal-overlay" (click)="cancelDelete()">
    <div class="confirm-modal" (click)="$event.stopPropagation()">
      <div class="confirm-icon"><i class="fas fa-exclamation-triangle"></i></div>
      <h3>Delete Scholarship?</h3>
      <p>This action cannot be undone. The scholarship and its template assignment will be permanently removed.</p>
      <div class="confirm-actions">
        <button type="button" class="btn btn-secondary" (click)="cancelDelete()">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="confirmDelete()">Yes, Delete</button>
      </div>
    </div>
  </div>
}
